import {useState} from 'react';
import {Button} from '../components/button';
import {Input} from '../components/input';
import {Label} from '../components/label';
import {Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle} from '../components/card';
import {Alert, AlertDescription} from '../components/alert';
import {AlertCircle, CheckCircle2, Clock} from 'lucide-react';
import {UserService} from "../services/UserService";
import {toast} from "sonner";

export function Register() {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: ''
    });
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (formData.password !== confirmPassword) {
            setError('Les mots de passe ne correspondent pas.');
            return;
        }

        setLoading(true);

        try {
            await UserService.createUser(formData);
            setTimeout(() => {
                setLoading(false);
                toast.success('Compte créé avec succès ! Vous pouvez maintenant vous connecter.', {
                    icon: <CheckCircle2 className="h-5 w-5"/>
                });
                setFormData({
                    firstName: '',
                    lastName: '',
                    email: '',
                    password: ''
                });
                setConfirmPassword('');
            }, 1000);
        } catch (err: any) {
            toast.error('Erreur lors de la création du compte.');
        }
    };

    const handleChange = (field: string, value: string) => {
        setFormData(prev => ({...prev, [field]: value}));
    };

    return (
        <div className="min-h-screen-header flex items-center justify-center bg-muted/30 p-4">
            <Card className="w-full max-w-2xl">
                <CardHeader className="space-y-1 text-center">
                    <div className="flex justify-center mb-4">
                        <div className="h-12 w-12 rounded-full bg-primary flex items-center justify-center">
                            <Clock className="h-6 w-6 text-primary-foreground"/>
                        </div>
                    </div>
                    <CardTitle>Créer un compte</CardTitle>
                    <CardDescription>
                        L’inscription est réservée aux responsables.
                    </CardDescription>
                </CardHeader>
                <form onSubmit={handleSubmit}>
                    <CardContent className="space-y-4">
                        {error && (
                            <Alert variant="destructive">
                                <AlertCircle className="h-4 w-4"/>
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="firstName">Prénom</Label>
                                <Input
                                    id="firstName"
                                    placeholder="Jean"
                                    value={formData.firstName}
                                    onChange={(e) => handleChange('firstName', e.target.value)}
                                    required
                                    disabled={loading}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="lastName">Nom</Label>
                                <Input
                                    id="lastName"
                                    placeholder="Dupont"
                                    value={formData.lastName}
                                    onChange={(e) => handleChange('lastName', e.target.value)}
                                    required
                                    disabled={loading}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="email">Adresse e-mail</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="jean.dupont@entreprise.com"
                                value={formData.email}
                                onChange={(e) => handleChange('email', e.target.value)}
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="password">Mot de passe</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder="••••••••"
                                    value={formData.password}
                                    onChange={(e) => handleChange('password', e.target.value)}
                                    required
                                    disabled={loading}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    placeholder="••••••••"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    </CardContent>

                    <CardFooter className="flex flex-col gap-4">
                        <Button type="submit" className="w-full mt-4" disabled={loading}>
                            {loading ? 'Création du compte...' : 'Créer un compte'}
                        </Button>
                    </CardFooter>
                </form>
            </Card>
        </div>
    );
}
